import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Fade,
  styled
} from '@mui/material';
import TokenLogo from './TokenLogo';

const SelectionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  '& > div': {
    flex: 1,
    minWidth: 0, // Prevents flex items from overflowing
  }
}));

const SelectionBox = styled(Box)(({ theme }) => ({
  background: '#f8f9fa',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: '#fff',
  },
  '& .selection-title': {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }
}));

const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 1,
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  }
}));

const NetworkOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  '& .network-icon': {
    width: 20,
    height: 20,
    flexShrink: 0
  },
  '& .network-name': {
    fontWeight: 500,
    fontSize: '0.875rem'
  }
}));

const TokenOption = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.2),
  minWidth: 0,
  '& .token-text': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  '& .token-symbol': {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.1
  },
  '& .token-name': {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));

// Network Icons Components
const BNBIcon = () => (
  <svg className="network-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#F3BA2F"/>
    <path d="M12.116 14.404L16 10.52l3.886 3.886 2.26-2.26L16 6l-6.144 6.144 2.26 2.26zM6 16l2.26-2.26L10.52 16l-2.26 2.26L6 16zm6.116 1.596L16 21.48l3.886-3.886 2.26 2.26L16 26l-6.144-6.144-2.26-2.26zm9.764-5.6L26 16l-2.26 2.26L21.48 16l2.26-2.26z" fill="white"/>
    <path d="M16 12.6l-2.26 2.26L16 17.12l2.26-2.26L16 12.6z" fill="white"/>
  </svg>
);

const BaseIcon = () => (
  <svg className="network-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#0052FF"/>
    <path d="M15.9998 26.6667C22.2588 26.6667 27.3332 21.5924 27.3332 15.3333C27.3332 9.07428 22.2588 4 15.9998 4C10.2735 4 5.53135 8.24709 4.95703 13.7778H18.5554V16.8889H4.95703C5.53135 22.4196 10.2735 26.6667 15.9998 26.6667Z" fill="white"/>
  </svg>
);

const SolanaIcon = () => (
  <svg className="network-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="url(#solanaGradient)"/>
    <defs>
      <linearGradient id="solanaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00FFA3"/>
        <stop offset="100%" stopColor="#DC1FFF"/>
      </linearGradient>
    </defs>
    <path d="M7.5 20.5L9.5 18.5H24.5L22.5 20.5H7.5Z" fill="white"/>
    <path d="M7.5 11.5L9.5 13.5H24.5L22.5 11.5H7.5Z" fill="white"/>
    <path d="M7.5 16L9.5 14H24.5L22.5 16H7.5Z" fill="white"/>
  </svg>
);

// Network configuration
const NETWORK_CONFIG = {
  // Define the order we want
  ORDER: ['bsc', 'base', 'solana'],
  
  // Display names and icons for networks
  NETWORKS: {
    'bsc': {
      name: 'BNB',
      icon: BNBIcon
    },
    'base': {
      name: 'BASE',
      icon: BaseIcon
    },
    'solana': {
      name: 'SOLANA',
      icon: SolanaIcon
    }
  }
};

const NetworkTokenSelector = ({ 
  availableTokens,
  selectedNetwork,
  selectedToken,
  onNetworkSelect,
  onTokenSelect
}) => {
  // Group tokens by network
  const tokensByNetwork = availableTokens.reduce((acc, token) => {
    if (!acc[token.network]) {
      acc[token.network] = [];
    }
    acc[token.network].push(token);
    return acc;
  }, {});

  // Get ordered network options
  const getOrderedNetworks = () => {
    const availableNetworks = Object.keys(tokensByNetwork);
    
    // Filter and order based on our defined order
    const orderedNetworks = NETWORK_CONFIG.ORDER.filter(network => 
      availableNetworks.includes(network)
    );
    
    // Add any networks not in our predefined order at the end
    const otherNetworks = availableNetworks.filter(network => 
      !NETWORK_CONFIG.ORDER.includes(network)
    );
    
    return [...orderedNetworks, ...otherNetworks];
  };

  // Get network config
  const getNetworkConfig = (network) => {
    return NETWORK_CONFIG.NETWORKS[network] || {
      name: network.toUpperCase(),
      icon: () => null
    };
  };

  const orderedNetworks = getOrderedNetworks();
  const getTokenById = (tokenId) => availableTokens.find(token => String(token.id) === String(tokenId));
  const getTokenLogoUri = (token) => token?.token_uri || token?.photo_uri || null;

  return (
    <SelectionContainer>
      <SelectionBox>
        <Typography className="selection-title">
          Payment Network
        </Typography>
        <StyledSelect fullWidth size="small">
          <InputLabel>Select Network</InputLabel>
          <Select
            value={selectedNetwork || ''}
            onChange={(e) => onNetworkSelect(e.target.value)}
            label="Select Network"
            renderValue={(selected) => {
              if (!selected) return '';
              const config = getNetworkConfig(selected);
              const IconComponent = config.icon;
              return (
                <NetworkOption>
                  <IconComponent />
                  <span className="network-name">{config.name}</span>
                </NetworkOption>
              );
            }}
          >
            <MenuItem value="">
              <em>Choose network</em>
            </MenuItem>
            {orderedNetworks.map(network => {
              const config = getNetworkConfig(network);
              const IconComponent = config.icon;
              return (
                <MenuItem key={network} value={network}>
                  <NetworkOption>
                    <IconComponent />
                    <span className="network-name">{config.name}</span>
                  </NetworkOption>
                </MenuItem>
              );
            })}
          </Select>
        </StyledSelect>
      </SelectionBox>

      {selectedNetwork && (
        <Fade in>
          <SelectionBox>
            <Typography className="selection-title">
              Payment Token
            </Typography>
            <StyledSelect fullWidth size="small">
              <InputLabel>Select Token</InputLabel>
              <Select
                value={selectedToken || ''}
                onChange={(e) => onTokenSelect(e.target.value)}
                label="Select Token"
                renderValue={(selected) => {
                  if (!selected) return '';
                  const token = getTokenById(selected);
                  if (!token) return '';
                  return (
                    <TokenOption sx={{ '& .token-name': { display: 'none' } }}>
                      <TokenLogo
                        uri={getTokenLogoUri(token)}
                        symbol={token.token_symbol}
                        size={18}
                      />
                      <Box className="token-text">
                        <span className="token-symbol">{token.token_symbol}</span>
                      </Box>
                    </TokenOption>
                  );
                }}
              >
                <MenuItem value="">
                  <em>Choose token</em>
                </MenuItem>
                {availableTokens
                  .filter(token => token.network === selectedNetwork)
                  .map(token => (
                    <MenuItem key={token.id} value={token.id}>
                      <TokenOption>
                        <TokenLogo
                          uri={getTokenLogoUri(token)}
                          symbol={token.token_symbol}
                          size={20}
                        />
                        <Box className="token-text">
                          <span className="token-symbol">{token.token_symbol}</span>
                          <span className="token-name">{token.token_name}</span>
                        </Box>
                      </TokenOption>
                    </MenuItem>
                  ))}
              </Select>
            </StyledSelect>
          </SelectionBox>
        </Fade>
      )}
    </SelectionContainer>
  );
};

export default NetworkTokenSelector;
